import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import Popover from '@material-ui/core/Popover';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import ApplicationsAction, {
   isSavingApplication,
   isFetchingApplicationSuccess,
   isFetchingApplication
} from 'pgdb-data-layer/lib/Redux/ApplicationsRedux';
import { APPLICATION_REGISTRATION_DRAFT, APPLICATION_STATUS_PENDING_TRAINING } from 'pgdb-data-layer/lib/Constants/Contact';
import Loading from '../../Components/Loading';
import StepIndicator from '../../Components/StepIndicator';
import { getToken } from '../../Session/SessionRedux';
import Navigator from '../../Navigation/Navigator';
import './Endorsements/ApplyEndorsement.scss';
import { STEPS_ENDORSEMENT_FORM } from './EndorsementTypePage';
import EndorsementTimeLimitNotice from './Endorsements/EndorsementTimeLimitNotice';
import ProfileEntry from './Profile/ProfileEntry';
import CheckboxInput from '../../Components/CheckboxInput';
import EndorsementActions, { getEndorsement, isFetchingEndorsement } from 'pgdb-data-layer/lib/Redux/EndorsementRedux';
import { APPLICATION_ENDORSEMENT, ENDORSEMENT_TYPE_PLUMBER, ENDORSEMENT_TYPE_DRAINLAYER } from 'pgdb-data-layer/lib/Helpers/Endorsement';
import LicenceActions, {
   getLicenceData,
   isFetchingLicence,
} from 'pgdb-data-layer/lib/Redux/LicenceRedux';
import { isFetchingContacts } from 'pgdb-data-layer/lib/Redux/ContactsRedux';
import { isFetchingUserInfo } from 'pgdb-data-layer/lib/Redux/UserInfoRedux';

const EndorsementDetailsPage = ({ token, isLoading, getLicences, licences, patchApplication, endorsement, patchApplicationSuccess, getApplicationPayload, isRenewal }) => {
   const { applicationId } = useParams();
   const [declaration, setDeclaration] = useState();
   const [navigateTo, setNavigateTo] = useState('');
   const [performedFooterOperation, setPerformedFooterOperation] = useState(false);
   const [isPatching, setIsPatching] = useState(false);
   const [insuranceAnchorEl, setInsuranceAnchorEl] = useState(null);

   // Navigate to training after patchApplication succeeds
   React.useEffect(() => {
      if (isPatching && patchApplicationSuccess && applicationId) {
          if (isRenewal) {
              Navigator.toRenewEndorsementTraining(applicationId);
          } else {
              Navigator.toEndorsementTraining(applicationId);
          }
      }
   }, [patchApplicationSuccess, applicationId, isPatching]);

   const currentEndorsement = endorsement && endorsement.find(e => e.applicationId === parseInt(applicationId));

   const readyToPerformFooterOperation = () => {
      if (!performedFooterOperation) {
         if (navigateTo === 'next') {
            if (currentEndorsement && currentEndorsement.applicationStatusCode !== APPLICATION_REGISTRATION_DRAFT) {
                if (isRenewal) {
                    Navigator.toRenewEndorsementTraining(applicationId);
                } else {
                    Navigator.toEndorsementTraining(applicationId);
                }
            } else {
               setIsPatching(true);
               patchApplication(token, applicationId, { ApplicationStatusCode: APPLICATION_STATUS_PENDING_TRAINING });
            }
         }
         if (navigateTo === 'exit') {
            Navigator.toEndorsements();
         }
         setPerformedFooterOperation(true);
      }
   };

   React.useEffect(() => {
      if (token && (!licences.currentLicences || licences.currentLicences.length === 0)) {
         getLicences(token);
      }
      if (token && applicationId) {
         getApplicationPayload(token, applicationId);
      }
   }, [token, applicationId]);

   React.useEffect(() => {
      if (endorsement && endorsement.length > 0 && applicationId) {
         if (currentEndorsement) {
            setDeclaration(currentEndorsement.endorsementDeclarationTimestamp ? true : false);
         }
      }
   }, [endorsement, applicationId]);

   const endorsementProfileDetailsHeader = () => {
      return (
         <div className="col s10 m10 l10 offset-l1">
            <div className="row profile-row">
               <div className="col l12">
                  <h6 className="subheader">
                     Profile and Public Register Details
                  </h6>
               </div>
            </div>
         </div>
      );
   };

   const footerGenerator = (onBackAction, onNextAction) => {
      return onSaveHandler => {
         return (
            <>
               <div className="col m8 l8 offset-l1">
                  <div className="row profile-row">
                     <div className="col l12">
                        <span className="section-header section-break">
                           Insurance declaration
                        </span>
                        <IconButton
                           size="small"
                           onClick={e => setInsuranceAnchorEl(e.currentTarget)}
                           style={{ marginLeft: '5px', verticalAlign: 'middle', padding: '0px', marginBottom: '3px' }}
                           aria-label="Insurance information"
                        >
                           <InfoIcon fontSize="small" />
                        </IconButton>
                        <Popover
                           open={Boolean(insuranceAnchorEl)}
                           anchorEl={insuranceAnchorEl}
                           onClose={() => setInsuranceAnchorEl(null)}
                           anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                           transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                        >
                           <div style={{ position: 'relative', padding: '16px', maxWidth: '480px' }}>
                              <IconButton
                                 size="small"
                                 onClick={() => setInsuranceAnchorEl(null)}
                                 aria-label="Close"
                                 style={{ position: 'absolute', top: '8px', right: '8px', padding: '2px' }}
                              >
                                 <CloseIcon fontSize="small" />
                              </IconButton>
                              <p style={{ margin: 0 }}>
                                 You must hold appropriate insurance cover to self‑certify.
                                 <br /><br />
                                 It is your responsibility to confirm your insurance covers the work you certify. This may be insurance you hold personally, or cover held by the business you work for. You must hold the right kind of insurance to cover you for work your self-certify, and your level of cover must be sufficient. You must also ensure you maintain appropriate insurance cover for the whole duration of your endorsement. This means checking with any new employer if you change the business you work for.
                                 <br /><br />
                                 When you certify work, you are confirming that the work complies with the building consent. It is your responsibility both to ensure the work does comply with the consent and that it is correctly inspected and certified. You and you alone will be liable if that is not the case and this is why you must hold appropriate insurance to do this.
                                 <br /><br />
                                 Before relying on your cover, check your policy or speak with your insurer or broker to confirm it applies to self‑certified work.
                              </p>
                           </div>
                        </Popover>
                     </div>
                  </div>
                  <div className="row profile-row">
                     <div className="col s10 m11 l12" style={{ marginTop: '10px' }}>
                        <CheckboxInput
                           id="insurance_declaration"
                           name="insuranceDeclaration"
                           label="I have and will maintain throughout the full term of my endorsement adequate and appropriate insurance cover for the work of certifying sanitary plumbing and/or drainlaying work for building consent purposes."
                           required
                           value={declaration}
                           onChange={() => setDeclaration(!declaration)} /* endorsement */
                           disabled={currentEndorsement && currentEndorsement.endorsementDeclarationTimestamp ? true : false}
                        />
                     </div>
                  </div>
                  <div className="row profile-row next-button-registration right">
                     <div className="navigate-buttons">
                        <button
                           type="button"
                           className="waves-light btn btn-inactive"
                           onClick={() => {
                              if (onBackAction) onBackAction();
                           }}
                        >
                           Back
                        </button>
                        <button
                           type="button"
                           className="waves-light btn btn-inactive btn-ml-30"
                           onClick={() => {
                              let saved = false;
                              if (onSaveHandler) saved = onSaveHandler();
                              if (saved) setNavigateTo('exit');
                           }}
                        >
                           Save and Exit
                        </button>
                        <button
                           type="button"
                           className="waves-effect waves-light btn btn-ml-30"
                           disabled={!declaration}
                           onClick={() => {
                              let saved = false;
                              if (onSaveHandler) saved = onSaveHandler();
                              if (saved && onNextAction) onNextAction();
                           }}
                        >
                           Next
                        </button>
                     </div>
                  </div>
               </div>
            </>
         );
      };
   };

   return (
      <main>
         <Loading isLoading={isLoading} overlayOnChildren={true}>
            <div className="section no-pad-bot" id="index-banner">
               <div className="container registration-container">
                  <div className="row no-margin flex">
                     <div className="col s10 m10 l10 offset-l1">
                        <div className="row profile-row">
                           <h4 className="columnheader">{isRenewal ? 'Endorsement Renewal' : 'Endorsement Application'}</h4>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <StepIndicator steps={STEPS_ENDORSEMENT_FORM} currentStep={2} />
            <EndorsementTimeLimitNotice />
            <div className="section no-pad-bot" id="index-banner">
               <ProfileEntry
                  header={endorsementProfileDetailsHeader()}
                  footer={footerGenerator(
                     () => isRenewal ? Navigator.toRenewEndorsementEdit(applicationId) : Navigator.toApplyEndorsementEdit(applicationId),
                     () => setNavigateTo('next')
                  )}
                  location={{}}
                  readyToPerformFooterOperation={readyToPerformFooterOperation}
                  disableLoading
               />
            </div>
         </Loading>
      </main>
   );
};

EndorsementDetailsPage.propTypes = {
   token: PropTypes.string.isRequired,
   isLoading: PropTypes.bool.isRequired,
   patchApplication: PropTypes.func.isRequired,
   endorsement: PropTypes.array.isRequired,
   licences: PropTypes.object.isRequired,
   getLicences: PropTypes.func.isRequired,
   patchApplicationSuccess: PropTypes.bool.isRequired,
   getApplicationPayload: PropTypes.func.isRequired,
   isRenewal: PropTypes.bool,
};

const mapStateToProps = state => ({
   isLoading: isFetchingApplication("patchApplication", state) || isFetchingApplication("getApplicationPayload", state) || isSavingApplication('saveApplication', state) || isFetchingUserInfo('saveUserContactInfo', state) || isFetchingEndorsement('getUserEndorsement', state),
   token: getToken(state),
   endorsement: state.application.payload.applications || [],
   licences: getLicenceData(state),
   patchApplicationSuccess: isFetchingApplicationSuccess('patchApplication', state) || false,
});

const mapDispatchToProps = {
   getLicences: LicenceActions.licenceRequest,
   patchApplication: ApplicationsAction.patchApplicationRequest,
   getApplicationPayload: ApplicationsAction.getApplicationPayloadRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(EndorsementDetailsPage);
